import FactDrawer from '@/cam-admin/control-panel/FactDrawer';
import { type FactGroup } from '@/karehero-app/tell-us-once/intake/FactList';
import { CARE_CIRCLE, type Carer } from '@/cam-admin/control-panel/fixtures';

/**
 * The carer's facts, as the same object the recipient's are.
 *
 * A phone number we do not hold is a `missing` row rather than an absent one,
 * which is how the profile treats a gap: a carer with no email on file is a
 * thing to go and get, and a row that simply is not there says nothing.
 */
const carerFacts = (carer: Carer): FactGroup[] => [
  {
    label: 'Details',
    rows: [
      {
        id: 'relationship',
        label: `Relationship to ${CARE_CIRCLE.recipientFirstName}`,
        value: carer.relationship,
      },
      {
        id: 'phone',
        label: 'Phone',
        value: carer.phone ?? '',
        ...(carer.phone ? {} : { from: 'missing' as const }),
      },
      {
        id: 'email',
        label: 'Email',
        value: carer.email ?? '',
        ...(carer.email ? {} : { from: 'missing' as const }),
      },
    ],
  },
];

/**
 * A carer's details, on the right over the frame.
 *
 * The same drawer the recipient gets from Edit. A carer and the person they
 * care for are both people on this circle whose details a care expert has to
 * be able to correct, so opening one should not be a read-only card while the
 * other is a form.
 *
 * The rail's carer rows used to carry the relationship, whether they are the
 * primary carer and the phone number on the row itself. At 400px that wrapped
 * to two lines a carer, and none of it is read until somebody is about to ring
 * them: the row is a name, and this is what is behind it.
 *
 * The carer data lives in ControlPanel, which renders this over the whole
 * frame, the same way the action and call drawers work.
 */
const CarerDrawer = ({
  open,
  carer,
  onSave,
  onClose,
}: {
  open: boolean;
  carer?: Carer;
  onSave: () => void;
  onClose: () => void;
}) => (
  <FactDrawer
    open={open}
    title={carer?.name ?? ''}
    subtitle={carer?.primary ? 'Primary carer' : undefined}
    groups={carer ? carerFacts(carer) : []}
    onSave={onSave}
    onClose={onClose}
  />
);

export default CarerDrawer;
